import { useMemo } from 'react';

import { Cart, CartItem } from '@givebacks/api/store';
import { DateTime } from 'luxon';
import {
  Badge,
  Box,
  ButtonMantine,
  Card,
  Divider,
  Grid,
  Group,
  InfoListCard,
  Stack,
  Text,
  Title,
  centsToDollar,
} from 'ui';

import { Printty } from '../../../../packages/Printty/printty';
import { getDate, toTitleCase } from '../../../utils';
import { OrderLineItem } from '../../molecules/OrderLineItem/OrderLineItem';
import { Item } from './Item';
import classes from './Order.module.css';
import { isDiscountCartItem, toItemDetailString } from './Order.utils';

type OrderSectionProps = {
  children: React.ReactNode;
  title?: string;
  action?: React.ReactNode;
};

const OrderSection = ({ title, action, children }: OrderSectionProps) => {
  return (
    <Stack gap="lg">
      {title && (
        <Group justify="space-between">
          <Title order={3}>{title}</Title>
          {action}
        </Group>
      )}
      <Stack gap="sm">{children}</Stack>
    </Stack>
  );
};

type OrderSummarySectionProps = {
  order: Pick<
    Cart,
    | 'subtotal'
    | 'fee'
    | 'total_without_tip'
    | 'cart_items'
    | 'options'
    | 'cart_options'
    | 'discount'
  >;
};

const OrderSummarySection = ({ order }: OrderSummarySectionProps) => {
  const cartCustomFields = order?.options ?? {};
  const cartCustomFieldInfo = order.cart_options;
  const hasCartCustomFields = !!Object.keys(cartCustomFields).length;

  const typeToFormatterMap = useMemo(() => {
    return {
      number: (value: number) => value,
      textfield: (value: string) => value,
      dollar: (value: number) => centsToDollar(value),
      address: (value: string) => value,
      dropdown: toItemDetailString,
      textarea: (value: string) => value,
      boolean: (value: boolean) => (value ? 'True' : 'False'),
      multiselect: (value: string[]) => value.join(', ') || 'None',
      date: (value: string) => DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED),
    };
  }, []);

  const formattedCustomFields = Object.keys(cartCustomFields).reduce((acc, key) => {
    const type = cartCustomFieldInfo?.find((field) => field.name === key)?.type;

    if (!type) {
      return acc;
    }

    const formatter = typeToFormatterMap[type];

    return {
      ...acc,
      [key]: formatter(cartCustomFields[key]),
    };
  }, {});

  const discountStoreItem = useMemo(
    () => order?.cart_items.find(isDiscountCartItem)?.store_item,
    [order?.cart_items]
  );

  return (
    <OrderSection title="Summary">
      <Card withBorder className={classes.OrderSummarySection__card}>
        {hasCartCustomFields && (
          <InfoListCard
            className={classes.OrderSummarySection__infoListCard}
            header={<InfoListCard.Header title="Cart Details" />}
            color="gray"
            direction="column"
            cols={1}
          >
            {Object.keys(formattedCustomFields).map((key) => {
              return (
                <InfoListCard.Item
                  key={key}
                  label={toTitleCase(`${key}:`)}
                  detail={formattedCustomFields[key]}
                />
              );
            })}
          </InfoListCard>
        )}

        <Stack gap={4} p="md">
          <Group justify="space-between">
            <Text type="sub">Subtotal</Text>
            <Text type="sub" fw={600}>
              {order?.subtotal ? centsToDollar(order?.subtotal) : centsToDollar(0)}
            </Text>
          </Group>

          {!!discountStoreItem && (
            <OrderLineItem.Discount
              couponCode={discountStoreItem?.name}
              discountAmount={`-${centsToDollar(order?.discount)}`}
            />
          )}

          <Group justify="space-between">
            <Text type="sub">Fees</Text>
            <Text type="sub" fw={600}>
              {order?.fee ? centsToDollar(order?.fee) : centsToDollar(0)}
            </Text>
          </Group>

          <Group justify="space-between">
            <Text type="sub" fw={600}>
              Order Total
            </Text>
            <Text type="sub" fw={600}>
              {order?.total_without_tip
                ? centsToDollar(order?.total_without_tip)
                : centsToDollar(0)}
            </Text>
          </Group>
        </Stack>
      </Card>
    </OrderSection>
  );
};

type OrderItemsSectionProps = {
  order: Pick<Cart, 'cart_items' | 'status'>;
  onFulfill?: (cartItemUuid: string | undefined, fulfilled: boolean) => void;
  hasCartWriteAccess: boolean;
};

const OrderItemsSection = ({
  order,
  onFulfill,
  hasCartWriteAccess = false,
}: OrderItemsSectionProps) => {
  const statusType = useMemo(() => {
    if (order?.status == 'purchased') return 'success';
    if (order?.status == 'refunded') return 'warning';
    return 'primary';
  }, [order?.status]);

  const items = useMemo(() => {
    return (order?.cart_items || []).filter((item) => !isDiscountCartItem(item));
  }, [order?.cart_items]);

  return (
    <OrderSection
      title="Items"
      action={
        <Badge type={statusType} variant="filled">
          {toTitleCase(order?.status)}
        </Badge>
      }
    >
      <Grid bg="gray.0" px="md" py="sm" gutter={2}>
        <Grid.Col span={4}>
          <Text type="caption" fw="500">
            Item
          </Text>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text type="caption" fw="500">
            Cost
          </Text>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text type="caption" fw="500">
            Qty
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text type="caption" fw="500">
            Total
          </Text>
        </Grid.Col>
      </Grid>

      {items.map((cartItem: CartItem, index) => (
        <Item
          key={cartItem.uuid}
          cartItem={cartItem}
          isLastItem={index === items.length - 1}
          isPartiallyRefunded={order?.status === 'partially_refunded'}
          hasCartWriteAccess={hasCartWriteAccess}
          onFulfill={onFulfill}
        />
      ))}
    </OrderSection>
  );
};

type OrderDetailsSectionProps = {
  order: Pick<Cart, 'payment_method' | 'notes' | 'created_at'>;
  paymentMethod: string;
  onOpenNewOrderNoteModal?: () => void;
};

const OrderDetailsSection = ({
  order,
  paymentMethod,
  onOpenNewOrderNoteModal,
}: OrderDetailsSectionProps) => {
  return (
    <OrderSection
      title="Details"
      action={
        onOpenNewOrderNoteModal ? (
          <Printty.NoPrintTarget>
            <ButtonMantine variant="subtle" size="sm" onClick={onOpenNewOrderNoteModal}>
              Add Notes
            </ButtonMantine>
          </Printty.NoPrintTarget>
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Stack p="md">
          <Text type="sub" fw={600}>
            Notes
          </Text>
          <Stack gap="lg">
            {order?.notes?.length > 0 ? (
              (order.notes || []).map((note) => (
                <Stack gap="xs" key={note.uuid}>
                  <Text type="caption" c="gray">
                    {note.text}
                  </Text>
                  <Text type="caption" c="gray.4" ta="right">
                    {getDate(note.created_at)}
                  </Text>
                </Stack>
              ))
            ) : (
              <Text type="caption" c="gray">
                None
              </Text>
            )}
          </Stack>
        </Stack>
        <Grid gutter="lg">
          <Grid.Col span="auto">
            <Stack p="md" gap={4}>
              <Text type="sub" fw={600}>
                Payment Method
              </Text>
              <Text type="caption" c="gray">
                {paymentMethod}
              </Text>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </OrderSection>
  );
};

interface OrderProps {
  order: Cart;
  onFulfill?: (cartItemUuid: string | undefined, fulfilled: boolean) => void;
  hasCartWriteAccess?: boolean;
  onOpenNewOrderNoteModal?: () => void;
  printRef?: React.RefObject<HTMLDivElement>;
}

const Order = ({
  order,
  onFulfill,
  hasCartWriteAccess = false,
  onOpenNewOrderNoteModal,
  printRef,
}: OrderProps) => {
  const paymentMethod = useMemo(() => {
    if (order?.payment_method == 'card_terminal') return 'Credit Card (POS)';
    if (order?.payment_method == 'cash') return 'Cash';
    if (order?.payment_method == 'pos_cash') return 'Cash (POS)';
    if (order?.payment_method == 'check') return 'Check';
    return 'Card';
  }, [order?.payment_method]);

  return (
    <Box ref={printRef} p="lg">
      <Stack gap="lg">
        <InfoListCard color="blue" cols={2} direction="column" enableUniformLabelWidths>
          <InfoListCard.Item
            label="Name:"
            detail={`${order.first_name} ${order.last_name}`.trim() || 'Not provided'}
          />
          <InfoListCard.Item label="Phone:" detail={order.phone_number.trim() || 'Not provided'} />
          <InfoListCard.Item label="Email:" detail={order.email.trim() || 'Not provided'} />
          <Box />
          <InfoListCard.Item
            label="Date:"
            detail={DateTime.fromISO(order.purchased_at || order.updated_at).toFormat(
              'MMMM d y, h:mm a'
            )}
          />
        </InfoListCard>

        <Divider size="lg" color="gray.0" />

        <OrderSummarySection order={order} />

        <Divider size="lg" color="gray.0" />

        <OrderItemsSection
          order={order}
          onFulfill={onFulfill}
          hasCartWriteAccess={hasCartWriteAccess}
        />

        <Divider size="lg" color="gray.0" />

        <OrderDetailsSection
          order={order}
          onOpenNewOrderNoteModal={onOpenNewOrderNoteModal}
          paymentMethod={paymentMethod}
        />
      </Stack>
    </Box>
  );
};

export { Order, type OrderProps };
